import React from 'react';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  BarProps,
} from 'recharts';

import useWindowDimensions from '~/hooks/dimensions';

import { Mobile, Description, Desktop } from './styles';

import RelatorioVendaCanalMetrica from '~/@types/RelatorioVendaCanalMetrica';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { GraficoProps } from './interface';

interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {}

interface CustomBarProps extends BarProps {
  payload?: RelatorioVendaCanalMetrica;
}

const exibicaoTotalPedidos = (pTotalPedidos: number) => {
  if (!pTotalPedidos) return 'Nenhum pedido';
  if (pTotalPedidos === 1) {
    return 'Um pedido';
  }
  return `${pTotalPedidos} pedidos`;
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (active && payload && Array.isArray(payload) && payload.length) {
    const [cPayload] = payload;

    if (cPayload) {
      const { payload: _payload } = cPayload;
      if (_payload) {
        const item: RelatorioVendaCanalMetrica = {
          ..._payload,
        };

        return (
          <Description>
            <i>
              <img
                src={`https://commerceplus.com.br/includes/bandeiras/${item.nome}.jpg`}
                alt={item.nome}
                title={item.nome}
              />
            </i>
            <div>
              <h2>{exibicaoTotalPedidos(item.pedidos)}</h2>
              <h3>{item.nome}</h3>
              <ins />
              <h4>
                TICKET MÉDIO:{' '}
                <b>
                  {item.ticketmedio.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </b>
              </h4>
              <h5>
                TOTAL:
                <b>
                  {item.total.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </b>
              </h5>
            </div>
          </Description>
        );
      }
    }
  }

  return null;
};

const CustomBarShapeDesktop: React.FC<CustomBarProps> = props => {
  const { x, y, width, height, payload } = props;

  if (payload) {
    const imageUrl = `https://commerceplus.com.br/includes/bandeiras/${payload.nome}.jpg`;

    if (!x || !y || !width || !height) return null;

    if (height <= 40) {
      return (
        <g>
          <rect
            x={x}
            y={Number(y)}
            width={width}
            height={height}
            fill="#555"
            rx={5}
          />
        </g>
      );
    }

    return (
      <g>
        <rect
          x={x}
          y={Number(y) + 20}
          width={width}
          height={height - 20}
          fill="#555"
          rx={5}
        />
        <defs>
          <clipPath id={`circular-clip-${payload.nome}`}>
            <circle
              cx={Number(x) + Number(width) / 2}
              cy={Number(y) + 20}
              r={20}
              width={40}
              height={40}
              overflow={'hidden'}
            />
          </clipPath>
        </defs>
        {imageUrl && (
          <image
            href={imageUrl}
            x={Number(x) + Number(width) / 2 - 20}
            y={Number(y)}
            width={40}
            height={40}
            clipPath={`url(#circular-clip-${payload.nome})`}
          />
        )}
      </g>
    );
  }
  return null;
};

const CustomBarShapeMobile: React.FC<CustomBarProps> = props => {
  const { x, y, width, height, payload } = props;

  if (payload) {
    const imageUrl = `https://commerceplus.com.br/includes/bandeiras/${payload.nome}.jpg`;

    if (!x || !y || !width || !height) return null;

    // if (width <= 10) {
    //   return (
    //     <g>
    //       <rect
    //         x={x}
    //         y={Number(y)}
    //         width={width}
    //         height={height}
    //         fill="#555"
    //         rx={5}
    //       />
    //     </g>
    //   );
    // }

    return (
      <g>
        <rect
          x={Number(x)}
          y={Number(y)}
          width={width}
          height={height}
          fill="#555"
          rx={5}
        />
        <defs>
          <clipPath id={`circular-clip-${payload.nome}`}>
            <circle
              cx={Number(x) + width}
              cy={Number(y) + height / 2}
              r={20}
              width={40}
              height={40}
              overflow={'hidden'}
            />
          </clipPath>
        </defs>
        {imageUrl && (
          <image
            href={imageUrl}
            x={Number(x) + width - 20}
            y={Number(y) + height / 2 - 20}
            width={40}
            height={40}
            clipPath={`url(#circular-clip-${payload.nome})`}
          />
        )}
      </g>
    );
  }
  return null;
};

const Grafico: React.FC<GraficoProps> = ({ canais }) => {
  const { width } = useWindowDimensions();

  let _height = canais.length * 100;
  if (_height <= 200) {
    _height = 200;
  }

  return (
    <>
      <Desktop>
        <ResponsiveContainer>
          <BarChart
            data={canais}
            margin={{
              top: 0,
              right: 10,
              left: 30,
              bottom: 25,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <YAxis
              tickFormatter={value =>
                `${value.toLocaleString('pt-br', {
                  style: 'currency',
                  currency: 'BRL',
                })}`
              }
              tick={{ fill: '#999', fontSize: 8 }}
            />

            <XAxis
              dataKey="nome"
              stroke={'#999'}
              fontSize={10}
              angle={0}
              textAnchor="end"
              tickMargin={10}
              dx={25}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(255,255,255, 0.1)' }}
            />

            <YAxis />
            <Tooltip />

            <Bar
              type="monotone"
              dataKey={'total'}
              radius={[6, 6, 0, 0]}
              fill="#666"
              shape={<CustomBarShapeDesktop dataKey={'total'} />}
            />
          </BarChart>
        </ResponsiveContainer>
      </Desktop>
      <Mobile
        style={{
          height: _height,
        }}
      >
        <ResponsiveContainer>
          <BarChart
            data={canais}
            layout="vertical"
            margin={{
              top: 10,
              right: 20,
              left: -40,
              bottom: 20,
            }}
            width={width}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis type="number" hide />

            <YAxis
              type="category"
              dataKey="nome"
              tick={false}
              tickLine={false}
              axisLine={false}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(255,255,255, 0.1)' }}
            />

            <Tooltip />

            <Bar
              dataKey={'total'}
              radius={[6, 6, 0, 0]}
              fill="#666"
              shape={<CustomBarShapeMobile dataKey={'total'} />}
            />
          </BarChart>
        </ResponsiveContainer>
      </Mobile>
    </>
  );
};

export default Grafico;
